import React, { useCallback, useMemo } from 'react';

import { Alert, FormControlLabel, Switch } from '@mui/material';
import {
  DataGridPro,
  GridCallbackDetails,
  GridColDef,
  GridFilterModel,
  GridToolbarContainer,
  GridToolbarFilterButton,
  getGridStringOperators,
} from '@mui/x-data-grid-pro';

import { FlightsCampaignsTableActionButtons } from './FlightsCampaignsTableActionButtons';

const common: Partial<GridColDef> = {
  flex: 1,
  filterOperators: getGridStringOperators().filter((operator) => operator.value === 'equals'),
};

interface Props {
  data: Array<App.FlightCampaign>;
  loading?: boolean;
  error?: string;
  onDelete: (id: string) => void;
  total: number;
  page: number;
  activeOnly: boolean;
  onActiveOnlyChange: (activeOnly: boolean) => void;
  activeFilters: GridFilterModel | undefined;
  onFilterChange: (model: GridFilterModel, details: GridCallbackDetails<'filter'>) => void;
  onPageChange: (page: number) => void;
}

export function FlightsCampaignsTable({
  data,
  loading,
  error,
  onDelete,
  total,
  page,
  activeOnly,
  onActiveOnlyChange,
  activeFilters,
  onFilterChange,
  onPageChange,
}: Props) {
  const columns: Array<GridColDef> = useMemo(() => {
    return [
      {
        ...common,
        field: 'id',
        headerName: 'Id',
      },
      {
        ...common,
        field: 'title',
        headerName: 'Title',
      },
      {
        ...common,
        field: 'type',
        headerName: 'Type',
      },
      {
        ...common,
        field: 'header',
        headerName: 'Header',
      },
      {
        ...common,
        field: 'region',
        headerName: 'Region',
      },
      {
        field: 'start_date',
        headerName: 'Start Date',
        filterable: false,
      },
      {
        field: 'end_date',
        headerName: 'End Date',
        filterable: false,
      },
      {
        field: 'brand',
        headerName: 'Brand',
        filterable: false,
        renderCell: (row) => {
          return (row.value as string)?.split(',').join(', ');
        },
      },
      {
        field: 'actions',
        headerName: 'Actions',
        renderCell: ({ row }) => <FlightsCampaignsTableActionButtons row={row} onDelete={onDelete} />,
        sortable: false,
        filterable: false,
      },
    ];
  }, [onDelete]);

  const Toolbar = useCallback(
    () => (
      <GridToolbarContainer>
        <GridToolbarFilterButton />
        <FormControlLabel
          control={
            <Switch
              checked={activeOnly}
              onChange={(event) => onActiveOnlyChange(event.target.checked)}
              inputProps={{ 'aria-label': 'Show active campaigns only' }}
            />
          }
          label="Active only"
        />
      </GridToolbarContainer>
    ),
    [activeOnly, onActiveOnlyChange],
  );

  if (error) {
    return <Alert severity="error">Something went wrong</Alert>;
  }

  return (
    <DataGridPro
      columns={columns}
      loading={loading}
      rows={data}
      rowCount={total}
      pagination
      paginationMode="server"
      paginationModel={{ page, pageSize: 20 }}
      pageSizeOptions={[20]}
      onPaginationModelChange={({ page }) => onPageChange(page)}
      filterMode="server"
      filterModel={activeFilters}
      onFilterModelChange={onFilterChange}
      disableRowSelectionOnClick
      disableColumnMenu
      density="standard"
      initialState={{ pinnedColumns: { right: ['actions'] } }}
      slots={{ toolbar: Toolbar }}
    />
  );
}
